import { isAxiosError } from "axios";

/**
 * Single-flight session refresh shared by the 401 interceptor, the session
 * query and the proactive timer.
 *
 * The API rotates the refresh token on every `/auth/refresh` call, so two
 * refreshes sent with the same cookie race each other: the loser gets a 401
 * and used to log the user out. Every caller in this tab therefore awaits the
 * same promise, and tabs coordinate through the Web Locks API when available,
 * or through a localStorage lease when it is not.
 */

const REFRESH_LOCK_NAME = "ilovemacumba:auth:refresh";
const LAST_REFRESH_AT_KEY = "ilovemacumba:auth:lastRefreshAt";
const REFRESH_LEASE_KEY = "ilovemacumba:auth:refreshLock";

export const REFRESH_TIMEOUT_MS = 10_000;
/** Outlives a refresh request so a crashed holder's lease eventually expires. */
const REFRESH_LEASE_TTL_MS = REFRESH_TIMEOUT_MS + 5_000;
/** localStorage has no compare-and-set: let a concurrent writer land before reading back. */
const LEASE_SETTLE_MS = 25;
const LEASE_POLL_MS = 100;
/** A refresh finished by another tab this recently already rotated our cookie. */
const RECENT_REFRESH_WINDOW_MS = 5_000;
/** Access tokens live 15 minutes on the API; refresh a little before that. */
export const PROACTIVE_REFRESH_AFTER_MS = 12 * 60 * 1000;
const PROACTIVE_CHECK_INTERVAL_MS = 60 * 1000;

let inFlight: Promise<void> | null = null;
let lastAuthenticatedAt = Date.now();
const tabId = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;

type LockManagerLike = {
  request: <T>(name: string, callback: () => Promise<T>) => Promise<T>;
};

const getLockManager = (): LockManagerLike | undefined => {
  if (typeof navigator === "undefined") return undefined;
  const locks = (navigator as Navigator & { locks?: LockManagerLike }).locks;
  return typeof locks?.request === "function" ? locks : undefined;
};

const readLastRefreshAt = (): number => {
  try {
    const raw = localStorage.getItem(LAST_REFRESH_AT_KEY);
    const parsed = raw ? Number(raw) : 0;
    return Number.isFinite(parsed) ? parsed : 0;
  } catch {
    return 0;
  }
};

const writeLastRefreshAt = (value: number): void => {
  try {
    localStorage.setItem(LAST_REFRESH_AT_KEY, String(value));
  } catch {
    // Storage can be unavailable (private mode); the in-tab flight still dedupes.
  }
};

type RefreshLease = { owner: string; expiresAt: number };

const readLease = (): RefreshLease | null => {
  try {
    const raw = localStorage.getItem(REFRESH_LEASE_KEY);
    if (!raw) return null;
    const lease = JSON.parse(raw) as Partial<RefreshLease>;
    return typeof lease.owner === "string" && typeof lease.expiresAt === "number"
      ? { owner: lease.owner, expiresAt: lease.expiresAt }
      : null;
  } catch {
    return null;
  }
};

const isHeldByAnotherTab = (lease: RefreshLease | null): boolean =>
  lease !== null && lease.owner !== tabId && lease.expiresAt > Date.now();

const sleep = (ms: number) =>
  new Promise<void>((resolve) => {
    setTimeout(resolve, ms);
  });

const tryAcquireLease = async (): Promise<"acquired" | "busy" | "unavailable"> => {
  if (isHeldByAnotherTab(readLease())) return "busy";
  try {
    localStorage.setItem(
      REFRESH_LEASE_KEY,
      JSON.stringify({ owner: tabId, expiresAt: Date.now() + REFRESH_LEASE_TTL_MS }),
    );
  } catch {
    return "unavailable";
  }
  await sleep(LEASE_SETTLE_MS);
  return readLease()?.owner === tabId ? "acquired" : "busy";
};

const releaseLease = (): void => {
  try {
    if (readLease()?.owner === tabId) localStorage.removeItem(REFRESH_LEASE_KEY);
  } catch {
    // Storage went away; the lease expires on its own.
  }
};

/** Resolves once no other tab holds a live lease (released or expired). */
const waitForOtherTabLease = (): Promise<void> =>
  new Promise((resolve) => {
    const onStorage = (event: StorageEvent) => {
      if (event.key === REFRESH_LEASE_KEY || event.key === null) check();
    };
    const intervalId = setInterval(() => check(), LEASE_POLL_MS);
    const check = () => {
      if (isHeldByAnotherTab(readLease())) return;
      clearInterval(intervalId);
      window.removeEventListener("storage", onStorage);
      resolve();
    };
    window.addEventListener("storage", onStorage);
    check();
  });

const runWithLease = async (run: () => Promise<void>): Promise<void> => {
  for (;;) {
    const state = await tryAcquireLease();
    if (state === "unavailable") return run();
    if (state === "acquired") {
      try {
        return await run();
      } finally {
        releaseLease();
      }
    }
    await waitForOtherTabLease();
  }
};

/**
 * Another tab may have rotated the shared cookie while our request was in
 * flight; then our 401 is stale, not an expired session.
 */
const refreshedByAnotherTab = async (since: number): Promise<boolean> => {
  if (isHeldByAnotherTab(readLease())) await waitForOtherTabLease();
  return readLastRefreshAt() > since;
};

/** True when the API rejected the refresh token itself (session is really over). */
export const isSessionRejected = (error: unknown): boolean => {
  const status = isAxiosError(error) ? error.response?.status : undefined;
  return status === 401 || status === 403;
};

export const markSessionAuthenticated = (): void => {
  lastAuthenticatedAt = Date.now();
};

export function refreshSessionOnce(
  perform: () => Promise<unknown>,
): Promise<void> {
  if (inFlight) return inFlight;

  const requestedAt = Date.now();
  const run = async () => {
    const lastRefreshAt = readLastRefreshAt();
    if (lastRefreshAt >= requestedAt - RECENT_REFRESH_WINDOW_MS) {
      markSessionAuthenticated();
      return;
    }
    try {
      await perform();
    } catch (error) {
      if (isSessionRejected(error) && (await refreshedByAnotherTab(lastRefreshAt))) {
        markSessionAuthenticated();
        return;
      }
      throw error;
    }
    const now = Date.now();
    writeLastRefreshAt(now);
    lastAuthenticatedAt = now;
  };

  const locks = getLockManager();
  const flight = (
    locks ? locks.request(REFRESH_LOCK_NAME, run) : runWithLease(run)
  ).finally(
    () => {
      if (inFlight === flight) inFlight = null;
    },
  );
  inFlight = flight;
  return flight;
}

/** Resolves once no refresh is running; never rejects. */
export const waitForSessionRefresh = async (): Promise<void> => {
  if (!inFlight) return;
  try {
    await inFlight;
  } catch {
    // The caller only needs the refresh to settle, not to succeed.
  }
};

export const abortSessionRefresh = (): void => {
  inFlight = null;
};

/**
 * Refreshes before the access token expires so long sessions don't depend on
 * a 401 arriving at a good moment (e.g. during a reload).
 */
export function startProactiveSessionRefresh(
  perform: () => Promise<unknown>,
): () => void {
  const maybeRefresh = () => {
    if (typeof document !== "undefined" && document.visibilityState === "hidden") {
      return;
    }
    if (Date.now() - lastAuthenticatedAt < PROACTIVE_REFRESH_AFTER_MS) return;
    refreshSessionOnce(perform).catch(() => {
      // A failed proactive refresh is handled by the next 401.
    });
  };

  const intervalId = setInterval(maybeRefresh, PROACTIVE_CHECK_INTERVAL_MS);
  const onVisibilityChange = () => maybeRefresh();
  document.addEventListener("visibilitychange", onVisibilityChange);

  return () => {
    clearInterval(intervalId);
    document.removeEventListener("visibilitychange", onVisibilityChange);
  };
}

/** Test helper */
export const resetSessionRefreshForTests = (): void => {
  inFlight = null;
  lastAuthenticatedAt = Date.now();
  try {
    localStorage.removeItem(LAST_REFRESH_AT_KEY);
    localStorage.removeItem(REFRESH_LEASE_KEY);
  } catch {
    // Ignore in environments without storage.
  }
};
